import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { isAllBranchesRole } from '../utils/auth';
import { getCurrentUser } from '../utils/auth';

const ALL_BRANCHES_VALUE = '';

function CustomerList({ companyId }) {
  const navigate = useNavigate();
  const currentUser = getCurrentUser();
  const canSelectAllBranches = isAllBranchesRole(currentUser?.role);

  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(10);
  const [syncing, setSyncing] = useState(false);

  // Branch filter state
  const [companies, setCompanies] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(companyId ? String(companyId) : '');

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (companyId) {
      setSelectedBranch(String(companyId));
    } else if (canSelectAllBranches) {
      setSelectedBranch(ALL_BRANCHES_VALUE);
    }
  }, [companyId, canSelectAllBranches]);

  useEffect(() => {
    fetchCustomers();
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedBranch]);

  const fetchCompanies = async () => {
    try {
      const response = await api.get('/companies');
      setCompanies(response.data || []);
    } catch (err) {
      console.error('Failed to fetch companies:', err);
    }
  };

  const handleBranchChange = (e) => {
    setSelectedBranch(e.target.value);
  };

  const getCompanyIdParam = () => {
    if (!canSelectAllBranches && !selectedBranch) return companyId || '';
    return selectedBranch === ALL_BRANCHES_VALUE ? '' : selectedBranch;
  };

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      const companyIdParam = getCompanyIdParam();
      if (companyIdParam) params.append('company_id', companyIdParam);

      const response = await api.get(`/customers?${params.toString()}`);
      setCustomers(response.data);
    } catch (err) {
      setMessage('Failed to load customers.');
    } finally {
      setLoading(false);
    }
  };

  const syncCustomers = async () => {
    setSyncing(true);
    setMessage('');
    try {
      const response = await api.post('/customers/sync/bc');
      const data = response.data;
      const details = [];
      if (data.processed !== undefined) details.push(`Processed: ${data.processed}`);
      if (data.inserted !== undefined) details.push(`Inserted: ${data.inserted}`);
      if (data.updated !== undefined) details.push(`Updated: ${data.updated}`);
      if (data.skipped !== undefined) details.push(`Skipped: ${data.skipped}`);

      setMessage(`Customers synced successfully! ${details.join(', ')}`);
      await fetchCustomers();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Failed to sync customers from Business Central.');
    } finally {
      setSyncing(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(customers.length / entriesPerPage);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = startIndex + entriesPerPage;
  const paginatedCustomers = customers.slice(startIndex, endIndex);

  const viewCustomerInAgedAR = (customerNo) => {
    navigate('/agingreports', { state: { customerSearch: customerNo } });
  };

  return (
    <div className="customer-list-container">
      <div className="page-header">
        <h1>Customers</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {canSelectAllBranches && (
            <select
              className="form-control"
              value={selectedBranch}
              onChange={handleBranchChange}
              style={{ minWidth: '180px', width: 'auto' }}
            >
              <option value="">All Branches</option>
              {companies.map((company) => (
                <option key={company.id} value={String(company.id)}>
                  {company.name}
                </option>
              ))}
            </select>
          )}
          <button
            className="btn btn-primary"
            onClick={syncCustomers}
            disabled={syncing}
            style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
          >
            {syncing ? 'Syncing...' : 'Refresh Customers'}
          </button>
        </div>
      </div>

      {message && (
        <div className={`alert ${message.includes('success') ? 'alert-success' : 'alert-error'}`}>
          {message}
        </div>
      )}

      <div className="card">
        <div className="form-group" style={{ marginBottom: '15px' }}>
          <input
            type="text"
            className="form-control"
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '300px', maxWidth: '100%' }}
          />
        </div>

        {loading ? (
          <div className="loading">Loading customers...</div>
        ) : customers.length === 0 ? (
          <div className="empty-state">No customers found.</div>
        ) : (
          <>
            <table className="table table-compact">
              <thead>
                <tr>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Customer No.</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Name</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Salesperson</th>
                  <th style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Payment Terms</th>
                  <th className="text-right" style={{ backgroundColor: '#e63740', color: '#ffffff' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {paginatedCustomers.map((customer) => (
                  <tr key={customer.id}>
                    <td>{customer.customer_no}</td>
                    <td>{customer.name}</td>
                    <td>{customer.salesperson_name || customer.salesperson_code || '-'}</td>
                    <td>{customer.due_date_text || '-'}</td>
                    <td className="text-right">
                      <span
                        onClick={() => viewCustomerInAgedAR(customer.customer_no)}
                        style={{ cursor: 'pointer', color: customer.balance && parseFloat(customer.balance) > 0 ? '#e63740' : '#5a9fd4', fontWeight: 'bold', textDecoration: 'underline' }}
                      >
                        {formatCurrency(customer.balance)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="pagination-controls">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={currentPage === 1}
                  className="btn btn-sm btn-primary"
                  style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
                >
                  Previous
                </button>

                <span className="pagination-info">
                  Page {currentPage} of {totalPages} ({customers.length} records)
                </span>

                <button
                  onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                  disabled={currentPage === totalPages}
                  className="btn btn-sm btn-primary"
                  style={{ backgroundColor: '#e63740', borderColor: '#e63740' }}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function formatCurrency(value) {
  if (value === undefined || value === null) return '0.00';
  const num = parseFloat(value);
  if (Number.isNaN(num)) return '0.00';
  return num.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default CustomerList;
